import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { SkeletonTable } from '../../components/ui/Skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table'
import { ADMIN_SCOPES, type AdminScope } from '../../lib/platformAdminAccess'
import * as team from '../../services/superAdminTeamService'

export default function PlatformTeamPage() {
  const { user } = useAuth()
  const { pushToast } = useToast()
  const [items, setItems] = useState<team.SuperAdminTeamRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [adminScope, setAdminScope] = useState<AdminScope>('ALL')
  const [submitting, setSubmitting] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [deactivateTarget, setDeactivateTarget] = useState<team.SuperAdminTeamRow | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await team.listSuperAdmins()
      setItems(data.items ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (!inviteOpen) {
      setInviteError(null)
      setEmail('')
      setFirstName('')
      setLastName('')
      setAdminScope('ALL')
      setSubmitting(false)
    }
  }, [inviteOpen])

  function closeInviteModal() {
    setInviteOpen(false)
  }

  async function submitInvite() {
    setSubmitting(true)
    setInviteError(null)
    setError(null)
    try {
      await team.createSuperAdmin({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        adminScope,
      })
      closeInviteModal()
      pushToast({ message: 'Platform operator invited — login details emailed', variant: 'success' })
      void refresh()
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : 'Invite failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function resetCredentials(row: team.SuperAdminTeamRow) {
    setBusyId(row.id)
    setError(null)
    try {
      await team.resetSuperAdminCredentials(row.id)
      pushToast({ message: 'New login details emailed', variant: 'success' })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed')
    } finally {
      setBusyId(null)
    }
  }

  async function confirmDeactivate() {
    if (!deactivateTarget) return
    setBusyId(deactivateTarget.id)
    setError(null)
    try {
      await team.deactivateSuperAdmin(deactivateTarget.id)
      pushToast({ message: 'Platform operator deactivated', variant: 'success' })
      setDeactivateTarget(null)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Deactivate failed')
    } finally {
      setBusyId(null)
    }
  }

  const initialLoading = loading && items.length === 0

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Platform team"
          description="Invite and manage super admins who can operate this console. Changes are recorded in the platform audit log."
          action={
            <Button type="button" onClick={() => {
              setInviteError(null)
              setInviteOpen(true)
            }}>
              Invite operator
            </Button>
          }
        />

        {error ? (
          <p className="mb-4 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        {initialLoading ? (
          <SkeletonTable rows={4} columns={6} />
        ) : (
          <div className="overflow-x-auto">
            <Table variant="plain">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead align="right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const isSelf = String(user?.id) === String(row.id)
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        {row.name}
                        {isSelf ? (
                          <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                        ) : null}
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.adminScope ?? 'ALL'}</TableCell>
                      <TableCell align="right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={busyId === row.id}
                            onClick={() => void resetCredentials(row)}
                          >
                            Reset login
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            disabled={busyId === row.id || isSelf}
                            title={isSelf ? 'You cannot deactivate your own account here' : undefined}
                            onClick={() => setDeactivateTarget(row)}
                          >
                            Deactivate
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Modal
        open={inviteOpen}
        onClose={() => {
          if (!submitting) closeInviteModal()
        }}
        title="Invite platform operator"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" disabled={submitting} onClick={closeInviteModal}>
              Cancel
            </Button>
            <Button type="button" disabled={submitting} onClick={() => void submitInvite()}>
              {submitting ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {inviteError ? (
            <p className="text-sm text-destructive" role="alert">
              {inviteError}
            </p>
          ) : null}
          <Input
            id="invite-email"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
          <Input
            id="invite-first"
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={submitting}
          />
          <Input
            id="invite-last"
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={submitting}
          />
          <label className="block space-y-1.5" htmlFor="invite-scope">
            <span className="text-sm font-medium text-foreground">Platform scope</span>
            <select
              id="invite-scope"
              className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground shadow-sm"
              value={adminScope}
              onChange={(e) => setAdminScope(e.target.value as AdminScope)}
              disabled={submitting}
            >
              {ADMIN_SCOPES.map((scope) => (
                <option key={scope} value={scope}>
                  {scope}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-muted-foreground">
            A temporary password is emailed. They sign in at the platform admin URL.
          </p>
        </div>
      </Modal>

      <Modal
        open={deactivateTarget != null}
        onClose={() => {
          if (busyId == null) setDeactivateTarget(null)
        }}
        title="Deactivate platform operator?"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setDeactivateTarget(null)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void confirmDeactivate()}>
              Deactivate
            </Button>
          </div>
        }
      >
        {deactivateTarget ? (
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{deactivateTarget.name}</span> will lose
            access to this console immediately. At least one other active operator must remain.
          </p>
        ) : null}
      </Modal>
    </div>
  )
}
